import { useEffect, useState } from 'react';
import { useShellRouter } from '@/hooks/useShellRouter';
import { getAuthToken } from '@/utils/authToken';
import { getLastVisitedRoute } from '@/utils/lastVisitedRoute';
import { consumePendingDeepLink } from '@/utils/pendingRoute';
import { hasHandoverToken } from '@/utils/handoverParams';
import { useSession } from '@/context/SessionContext';
import { httpFetch } from '@/lib/httpClient';
import { reportError } from '@/observability/clientLogger';
import { config } from '@/config/runtime';
import AppLoader from '@/components/AppLoader';

export default function HomeRedirect() {
  const router = useShellRouter();
  const [isRedirecting, setIsRedirecting] = useState(false);
  // Tenant from the session (/api/me) — not decoded from a token in the browser.
  const { tenant } = useSession();

  useEffect(() => {
    // Handle a handover token from query params first (login flow). Either name —
    // the SPA's onlyMicroapps redirect sends ?userToken=, everything else ?authToken=.
    const urlParams = new URLSearchParams(window.location.search);

    if (hasHandoverToken(urlParams)) {
      // Let AuthGuard handle token storage, then this effect will re-run
      return;
    }

    // Wait for the session to resolve before deciding where to send the user.
    if (!tenant) return;

    if (isRedirecting) return;
    setIsRedirecting(true);

    const determineRedirect = async () => {
      // The page the user was actually trying to reach when a guard sent them to sign in
      // — an emailed link such as /class?imo=9735062. Read once and cleared: the sign-in
      // round trip normally returns them straight there via returnUrl, and this is the
      // fallback for the paths that lose it (see utils/pendingRoute).
      const pendingDeepLink = consumePendingDeepLink();
      const pendingPath = pendingDeepLink ? pendingDeepLink.split(/[?#]/)[0] : null;

      try {
        // URL param takes priority over localStorage. The chat interface appends
        // ?lastVisitedRoute= when opening micro-apps in a new tab so Safari (which
        // partitions iframe localStorage) still lands on the correct page.
        //
        // That value now carries a query of its own — the vessel travels in it for the
        // same partitioning reason (see utils/vesselHandover, applied at bridge init) —
        // so match and navigate on the PATH. Keeping the query here would only miss the
        // allow-list, which is how a handover would land the user on /overview instead.
        const routeFromUrl = urlParams.get('lastVisitedRoute');
        const lastVisitedRoute = ((routeFromUrl && routeFromUrl.startsWith('/'))
          ? routeFromUrl
          : getLastVisitedRoute())?.split(/[?#]/)[0] ?? null;

        // localhost → Bearer header; deployed → null, and the HttpOnly cookie authenticates.
        const authToken = getAuthToken();
        const nodeEnv = config.nodeEnv;
        const response = await httpFetch(`/api/marketplace/apps?nodeEnv=${nodeEnv}`, {
          headers: {
            ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
            'x-node': tenant,
          },
        });

        if (response.ok) {
          const data = await response.json();
          const appsList = data.apps || data;

          // Build a set of allowed hrefs for quick lookup
          const allowedHrefs = new Set(appsList.map((app: { href: string }) => app.href));
          // Always allow /overview if user has overview access
          if (data.hasOverviewAccess) {
            allowedHrefs.add('/overview');
          }

          // The pending deep link outranks the last visited route: it is an explicit
          // request the user made minutes ago, not a leftover from a previous session.
          // /view/<id> is matched by prefix, exactly as RouteGuard matches it — the
          // allow-list carries one representative id, not every document.
          const pendingAllowed = !!pendingPath && (
            allowedHrefs.has(pendingPath) ||
            (pendingPath.startsWith('/view/') &&
              Array.from(allowedHrefs).some(href => String(href).startsWith('/view/')))
          );
          if (pendingDeepLink && pendingAllowed) {
            router.replace(pendingDeepLink);
            return;
          }

          // Agent shell home — prefer Agent Hub over legacy /overview (remote often down locally).
          if (lastVisitedRoute && allowedHrefs.has(lastVisitedRoute)) {
            router.replace(lastVisitedRoute);
            return;
          }

          if (allowedHrefs.has('/survey') || allowedHrefs.has('/class')) {
            // Prefer hub landing; Survey is reachable from Ship Management.
            router.replace('/agent-hub');
            return;
          }

          // If user has overview access, redirect to overview
          if (data.hasOverviewAccess) {
            router.replace('/overview');
            return;
          }

          // Otherwise redirect to the first available app
          if (appsList.length > 0) {
            router.replace(appsList[0].href);
            return;
          }

          router.replace('/agent-hub');
          return;
        }

        // Fallback: honour deep link, else Agent Hub (not /overview — that remote is often offline in agent-shell dev).
        router.replace(pendingDeepLink || '/agent-hub');
      } catch (error) {
        // The user lands on Agent Hub instead of where they were — a wrong-page
        // report, not an error report, without this line.
        reportError(error, { event_detail: 'index_redirect_failed' });
        router.replace(pendingDeepLink || '/agent-hub');
      }
    };

    determineRedirect();
  }, [router, isRedirecting, tenant]);

  return (
    <AppLoader />
  );
}
